// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import type { YearTotal } from '../types';
import { el } from '../dom';
import { formatMass, formatYear } from '../format';
import { MEDIUM_COLORS } from '../colors';

export function renderTrends(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'National trends'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'Total reported emissions over time, split by ',
    el('span', { class: 'glossary-link', 'data-term': 'point_source', tabindex: '0' }, 'medium'),
    '. Big drops often reflect facility closures or improved measurement; big rises are worth investigating.'
  ]));

  const totals = ctx.data.yearTotals;
  if (totals.length === 0) {
    root.appendChild(el('p', { class: 'text-tertiary' }, 'No yearly totals available.'));
    return;
  }

  const card = el('div', { class: 'card mb-lg' });
  card.appendChild(el('h2', { class: 'card-title' }, 'Total release per year (kg)'));
  card.appendChild(el('p', { class: 'card-subtitle' }, 'Stacked bars by medium. Hover for exact values.'));
  card.appendChild(buildStackedBars(totals));
  card.appendChild(el('div', { class: 'legend mt-lg' }, [
    legendItem(MEDIUM_COLORS.air, 'Air'),
    legendItem(MEDIUM_COLORS.water, 'Water'),
    legendItem(MEDIUM_COLORS.land, 'Land')
  ]));
  root.appendChild(card);

  // Year-over-year change table
  const tbl = el('div', { class: 'card' });
  tbl.appendChild(el('h2', { class: 'card-title' }, 'Year-over-year change'));
  tbl.appendChild(el('p', { class: 'card-subtitle' }, 'How national totals shifted from one reporting year to the next.'));
  const wrap = el('div', { class: 'table-wrap' });
  const t = el('table', { class: 'data' }, [
    el('thead', null, [el('tr', null, [
      el('th', null, 'Year'),
      el('th', null, 'Air'),
      el('th', null, 'Water'),
      el('th', null, 'Land'),
      el('th', null, 'Total'),
      el('th', null, 'Δ vs prior')
    ])]),
    el('tbody', null,
      totals.slice().reverse().map((r, idx, arr) => {
        const prev = arr[idx + 1];
        const cur = r.air + r.water + r.land;
        const prevT = prev ? prev.air + prev.water + prev.land : null;
        const pct = prevT ? ((cur - prevT) / prevT) * 100 : null;
        return el('tr', null, [
          el('td', { class: 'num' }, formatYear(r.year)),
          el('td', { class: 'num' }, formatMass(r.air)),
          el('td', { class: 'num' }, formatMass(r.water)),
          el('td', { class: 'num' }, formatMass(r.land)),
          el('td', { class: 'num' }, formatMass(cur)),
          el('td', { class: 'num', style: pct == null ? '' : pct > 0 ? 'color: var(--status-bad)' : 'color: var(--status-good)' },
            pct == null ? '—' : (pct > 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`))
        ]);
      })
    )
  ]);
  wrap.appendChild(t);
  tbl.appendChild(wrap);
  root.appendChild(tbl);
}

function buildStackedBars(rows: YearTotal[]): HTMLElement {
  const W = 920, H = 320, padL = 56, padR = 16, padT = 16, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(...rows.map(r => r.air + r.water + r.land));
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = document.createElementNS(ns, 'svg');
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svgEl.setAttribute('class', 'chart');
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Gridlines + y labels
  for (let i = 0; i <= 4; i++) {
    const t = i / 4;
    const y = padT + innerH * (1 - t);
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', String(padL));
    line.setAttribute('x2', String(W - padR));
    line.setAttribute('y1', String(y));
    line.setAttribute('y2', String(y));
    line.setAttribute('stroke', 'var(--border-subtle)');
    line.setAttribute('stroke-dasharray', i === 0 ? '0' : '2,3');
    svgEl.appendChild(line);
    const lab = document.createElementNS(ns, 'text');
    lab.setAttribute('x', String(padL - 6));
    lab.setAttribute('y', String(y + 3));
    lab.setAttribute('text-anchor', 'end');
    lab.setAttribute('font-size', '10');
    lab.setAttribute('fill', 'var(--text-tertiary)');
    lab.textContent = formatMass(max * t);
    svgEl.appendChild(lab);
  }

  const barW = innerW / rows.length;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const total = r.air + r.water + r.land;
    let cum = 0;
    const segments: Array<{ k: keyof YearTotal; v: number; color: string }> = [
      { k: 'land', v: r.land, color: MEDIUM_COLORS.land },
      { k: 'water', v: r.water, color: MEDIUM_COLORS.water },
      { k: 'air', v: r.air, color: MEDIUM_COLORS.air }
    ];
    const xBase = padL + i * barW + 1;
    for (const seg of segments) {
      const h = (seg.v / max) * innerH;
      const y = padT + innerH - cum - h;
      cum += h;
      const rect = document.createElementNS(ns, 'rect');
      rect.setAttribute('x', String(xBase));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(Math.max(1, barW - 2)));
      rect.setAttribute('height', String(h));
      rect.setAttribute('fill', seg.color);
      const tip = `${formatYear(r.year)} · ${seg.k} · ${formatMass(seg.v)} (of ${formatMass(total)})`;
      rect.setAttribute('data-tip', tip);
      rect.setAttribute('aria-label', tip);
      svgEl.appendChild(rect);
    }
    if (r.year % 5 === 0) {
      const lab = document.createElementNS(ns, 'text');
      lab.setAttribute('x', String(xBase + barW / 2 - 1));
      lab.setAttribute('y', String(H - padB + 14));
      lab.setAttribute('text-anchor', 'middle');
      lab.setAttribute('font-size', '10');
      lab.setAttribute('fill', 'var(--text-tertiary)');
      lab.textContent = String(r.year);
      svgEl.appendChild(lab);
    }
  }
  const wrap = document.createElement('div');
  wrap.appendChild(svgEl);
  return wrap;
}

function legendItem(color: string, label: string): HTMLElement {
  return el('span', null, [
    el('span', { class: 'legend-swatch', style: `background:${color}` }),
    label
  ]);
}
